import m from "mithril";
import client from "@shared/utils/client-rest";
import xlsx from "xlsx-js-style";
import dayjs from "dayjs";
import dayjs_utc from "dayjs/plugin/utc";
import dayjs_tz from "dayjs/plugin/timezone";
dayjs.extend(dayjs_utc);
dayjs.extend(dayjs_tz);

import cloneDeep from "lodash/cloneDeep";
import { maximizeID, minimizeID } from "@shared/utils/uuid-compression";
import NotifyModel from "@app/models/notify";

import { numeralFormat, convertToValidFilename } from "@app/helpers/formatting";
import { atspmApiBase, awaitAtspmRouting } from "@app/helpers/atspm-api-version";
import { applyOptimusIndications, transformSpaceTimeData } from "@app/helpers/space-time-utils";
import OptimusFineTuningModel from "@app/models/optimus-fine-tuning";
import OptimusModel from "#frontend/models/optimus/optimus-jobs/optimus-jobs.ts";
import { OPTIMIZATION_TYPE } from "@shared/constants";

export const TIMING_PLAN_DOWNLOAD_FIELDS = [
	{
		title: "Signal ID",
		source: "signal",
		key: "external_signal_id",
	},
	{
		title: "Signal Name",
		source: "signal",
		key: "name_short",
	},
	{
		title: "Offset Before",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.offsets?.old ?? "--",
	},
	{
		title: "Offset After",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.offsets?.new ?? "--",
	},
	{
		title: "Phase 1 Split Before",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["1"]?.old ?? "--",
	},
	{
		title: "Phase 1 Split After",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["1"]?.new ?? "--",
	},
	// dot it for all phases
	{
		title: "Phase 2 Split Before",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["2"]?.old ?? "--",
	},
	{
		title: "Phase 2 Split After",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["2"]?.new ?? "--",
	},
	{
		title: "Phase 3 Split Before",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["3"]?.old ?? "--",
	},
	{
		title: "Phase 3 Split After",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["3"]?.new ?? "--",
	},
	{
		title: "Phase 4 Split Before",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["4"]?.old ?? "--",
	},
	{
		title: "Phase 4 Split After",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["4"]?.new ?? "--",
	},
	{
		title: "Phase 5 Split Before",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["5"]?.old ?? "--",
	},
	{
		title: "Phase 5 Split After",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["5"]?.new ?? "--",
	},
	{
		title: "Phase 6 Split Before",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["6"]?.old ?? "--",
	},
	{
		title: "Phase 6 Split After",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["6"]?.new ?? "--",
	},
	{
		title: "Phase 7 Split Before",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["7"]?.old ?? "--",
	},
	{
		title: "Phase 7 Split After",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["7"]?.new ?? "--",
	},
	{
		title: "Phase 8 Split Before",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["8"]?.old ?? "--",
	},
	{
		title: "Phase 8 Split After",
		source: "job",
		key: (signal_id, signals) =>
			signals?.find((signal) => signal.signal_id === signal_id)?.results?.splits?.["8"]?.new ?? "--",
	},
];

export const TSD_VIEWS = [
	{ id: "historical", name: "Historical (ATSPM)" },
	{ id: "aggregated", name: "Aggregated" },
];

// The two ways the bars of an aggregated diagram can be built, in Abacus and on an Optimus job alike.
// 'programmed' lays the ring out from a timing plan — every phase holding its split, with its yellow, red
// clearance and the protected left the lead/lag toggles act on. In Abacus that plan is estimated from
// ATSPM (a signal with no resolvable plan reads as No Data); on a job it is the optimized plan itself.
// 'actual' marks where green is likely rather than the programmed switch points: in Abacus the
// config → ATSPM → probe pipeline with the through alone, on a job the bands the optimizer returned.
export const TSD_SOURCES = [
	{ id: "programmed", name: "Programmed Timing" },
	{ id: "actual", name: "Green Probability" },
];

class SpaceTime {
	constructor() {
		// Which diagram the consolidated Time Space Diagram panel shows (inline and expanded), and which
		// approach the aggregated one is built with.
		this.view = TSD_VIEWS[0].id;
		// Abacus builds on measured data first and only augments it with config, so it opens on Green
		// Probability; a job's own plan is the point of its diagram, so it opens on Programmed Timing.
		this.aggregated_source = "actual";
		this.optimus_source = "programmed";
		// Per-signal left-turn re-sequencing chosen in the offset editor (lead-lead / lead-lag / …), and the
		// event-derived sequence estimates (signal_id → { rings, confidence }) that label the current order.
		this.sequences = {};
		this.sequence_estimates = {};
		this.data = null;
		this.init_data = null;
		this.current_data = null;
		this.before_data = null;
		this.undo_data = null;
		this.redo_data = null;
		// Which side of an Optimus job the diagram is showing. A job's two sides are separate stored
		// payloads rather than a difference between rows, so the column headings read this rather than
		// comparing each signal's offset. A job loads on its optimized side.
		this.offset_side = "after";
		this.showModal = false;
		this.loading = false;
		this.time = 1;
		this.original_network = null;
		this.loading_optimus = false;
		this.prefs_loading = true;
		this.not_show_optimize = false;
		this.default_new_job_settings = {
			// customer_id added during runtime
			// network_id added during runtime
			// tod_id added during runtime
			// signals::UUID[] added during runtime
			optimization_type: OPTIMIZATION_TYPE.offset,
			weights: {
				offset: {
					aoy_weight: 1.0,
				},
			},
		};
		// NB: Unlike prefs, current_new_job_settings contains runtime-specified job params
		this.current_new_job_settings = null;
		this.job_tod_id = null;
		this.loading_job = false;
		this.loading_job_done = false;
		this.req_seq = 0; // debug request sequence for updateTimeSpaceDiagram
		this.latest_req_seq = 0; // last request intended to win (ignore stale responses)

		// ─── Programmed Timing plan editor ───
		// Per-signal edits to the ATSPM-inferred plan (keyed by minimized signal id), the routes
		// re-derived from them, and which base data that re-derivation was made against — a new fetch
		// invalidates it. Rebuilding from the DB takes over a minute, so an edit is applied by the
		// server's pure recompute path instead and held here.
		this.plan_overrides = {};
		this.plan_adjusted_charts = null;
		this.plan_base_ref = null;
		this.plan_editor_open = false;
		this.plan_applying = false;
		// Whether the network being viewed has ATSPM at all; the view selector drops Historical without it.
		this.network_has_atspm = true;
		// The chart payload the current session was seeded from. Abacus and an Optimus job share this
		// model, so the panel re-seeds whenever the model last belonged to something else.
		this.seeded_from = null;
	}

	seedFromCharts = (charts) => {
		this.clearSession();
		this.setData(transformSpaceTimeData(charts));
		this.setCurrentData(charts);
		this.setInitData(charts);
		this.seeded_from = charts;
	};

	setNetworkHasAtspm = (has_atspm) => (this.network_has_atspm = has_atspm !== false);

	setPlanEditorOpen = (open) => (this.plan_editor_open = open);

	// The charts the aggregated diagram should draw: the plan-adjusted copy when it belongs to this base
	// data, otherwise the base data itself.
	planChartsFor = (base_charts) => (this.plan_base_ref === base_charts ? this.plan_adjusted_charts : null) || base_charts;

	clearPlanOverrides = () => {
		this.plan_overrides = {};
		this.plan_adjusted_charts = null;
		this.plan_base_ref = null;
	};

	// Re-derive the diagram from edited plans. The server rebuilds the bands from each row's inferred_plan
	// (no DB read), so this returns in milliseconds rather than re-running the whole TSD query.
	applyPlanOverrides = async (base_charts, overrides) => {
		this.plan_overrides = overrides || {};
		if (!Array.isArray(base_charts) || !Object.keys(this.plan_overrides).length) {
			this.plan_adjusted_charts = null;
			this.plan_base_ref = null;
			this.plan_editor_open = false;
			m.redraw();
			return;
		}

		this.plan_applying = true;
		m.redraw();

		// The fetch reverses the upstream route's signals for the FE's index-for-index pairing; the API
		// expects them in its own order, so un-reverse on the way out and re-reverse on the way back.
		const request_data = cloneDeep(base_charts);
		if (request_data.length > 1 && Array.isArray(request_data[1]?.signals)) request_data[1].signals.reverse();

		try {
			const res = await client(`/api/v1/pages/optimus-v2/update-offsets`, {
				method: "POST",
				body: { current_data: request_data, plan_overrides: this.plan_overrides, job_id: this.job_id },
			});
			if (Array.isArray(res) && res.length > 1 && Array.isArray(res[1]?.signals)) res[1].signals.reverse();
			this.plan_adjusted_charts = res;
			this.plan_base_ref = base_charts;
			this.plan_editor_open = false;
			// The inline offset editor draws from this.data, so it has to move with the override too —
			// otherwise the panel keeps showing the pre-edit bands.
			this.setData(transformSpaceTimeData(res));
			this.setCurrentData(res);
			this.seeded_from = res;
		} catch (err) {
			NotifyModel.showNotify(err?.message || "Could not apply the timing plan changes", true);
		} finally {
			this.plan_applying = false;
			m.redraw();
		}
	};

	// `keep_redo`: a switch to the BEFORE side must not overwrite the AFTER snapshot with its own result,
	// or the AFTER heading has nothing left to return to.
	updateTimeSpaceDiagram = async (callback, offsets, { keep_redo = false } = {}) => {
		const { current_data, data } = this;
		let new_offsets = {};
		const seq = ++this.req_seq;
		this.latest_req_seq = seq; // last-writer-wins
		console.log("[TSD] updateTimeSpaceDiagram:start", { seq, passedOffsets: !!offsets, job_id: this.job_id });

		if (!offsets) {
			// Handle both new and legacy structure
			const downstreamSignals = data?.downstream?.signals || data?.downstreamData;
			// Build offsets by signal_id - since one signal can control multiple intersections,
			// we need to ensure we only send one offset per signal_id
			downstreamSignals?.forEach((i) => {
				// Skip crossing / no-data rows — they carry no signal or no offset to send.
				if (i.signal_id == null || i.plan_offset == null) return;
				// Only update if this signal_id hasn't been set yet, or if we're overwriting with the same value
				// This ensures consistency when one signal controls multiple intersections
				if (new_offsets[i.signal_id] === undefined) {
					new_offsets[i.signal_id] = parseInt(i.plan_offset, 10);
				} else if (new_offsets[i.signal_id] !== parseInt(i.plan_offset, 10)) {
					// Log a warning if we encounter different offsets for the same signal
					console.warn("[TSD] Multiple intersections with same signal_id have different offsets", {
						signal_id: i.signal_id,
						existing_offset: new_offsets[i.signal_id],
						new_offset: parseInt(i.plan_offset, 10)
					});
					// Use the latest value (last intersection wins)
					new_offsets[i.signal_id] = parseInt(i.plan_offset, 10);
				}
			});

			// One-way couplet rows carry a signal that only exists in the upstream direction
			// (e.g. SOP 2 paired with SOP 1). Add those too — but never overwrite a downstream
			// entry, since edits to shared signals are only synced within downstreamData.
			const upstreamSignals = data?.upstream?.signals || data?.upstreamData;
			upstreamSignals?.forEach((i) => {
				if (i.signal_id != null && new_offsets[i.signal_id] === undefined && i.plan_offset != null) {
					new_offsets[i.signal_id] = parseInt(i.plan_offset, 10);
				}
			});
			console.log("[TSD] Built offsets from current data", { seq, new_offsets });
		} else {
			new_offsets = offsets;
			console.log("[TSD] Using passed offsets", { seq, new_offsets });
		}

		this.loading = true;

		// Create a deep copy of current_data to avoid modifying the original
		const requestData = JSON.parse(JSON.stringify(current_data));
		console.log("[TSD] requestData snapshot", { seq, current_data: !!current_data, signals: requestData?.[1]?.signals?.length });

		// Reverse the signals array in the second element if it exists
		if (
			Array.isArray(requestData) &&
			requestData.length > 1 &&
			requestData[1] &&
			Array.isArray(requestData[1].signals)
		) {
			requestData[1].signals = requestData[1].signals.reverse();
		}

		// Update to use the TSD-V2 endpoint
		const requestBody = {
			current_data: requestData,
			new_offsets: new_offsets,
			new_sequences: this.sequences,
			network_id: this.original_network?.id,
			job_id: this.job_id,
            seq,
		};
		console.log("[TSD] API request body", { seq, requestBody: JSON.stringify(requestBody, null, 2) });

		return client(`/api/v1/pages/optimus-v2/update-offsets`, {
			method: "POST",
			body: requestBody,
		})
			.then(async (res) => {
				if (seq !== this.latest_req_seq) {
					console.log("[TSD] ignoring stale response", { seq, latest: this.latest_req_seq });
					return;
				}
				console.log("[TSD] updateTimeSpaceDiagram:response", { seq, ok: true, downstreamSignals: Array.isArray(res) && res?.[1]?.signals?.length });

				// Log what we got back from the API
				const responseOffsets = {};
				if (Array.isArray(res) && res.length > 0 && res[0] && Array.isArray(res[0].signals)) {
					res[0].signals.forEach(signal => {
						responseOffsets[signal.signal_id] = signal.plan_offset;
					});
				}
				console.log("[TSD] API response offsets", { seq, sent: new_offsets, received: responseOffsets });

				// Reverse the signals array in the response if it exists
				if (Array.isArray(res) && res.length > 1 && res[1] && Array.isArray(res[1].signals)) {
					res[1].signals = res[1].signals.reverse();
				}

				// The update endpoint returns the same two-colour payload the job did, so the derived
				// indications have to be rebuilt on it or a single offset edit would flatten the bars.
				applyOptimusIndications(res, this.optimus_plan, this.optimusSplitDelta, this.optimus_source, this.optimusSequences(), this.sequences);

				const data = await transformSpaceTimeData(res);

				// Log what offsets are in the transformed data
				const transformedOffsets = {};
				const downstreamSignals = data?.downstream?.signals || data?.downstreamData;
				downstreamSignals?.forEach(signal => {
					transformedOffsets[signal.signal_id] = signal.plan_offset;
				});
				console.log("[TSD] Transformed data offsets", { seq, transformedOffsets });

				await this.setData(data);
				await this.setCurrentData(res);
				console.log("[TSD] updateTimeSpaceDiagram:applied", { seq, offsetsCount: Object.keys(new_offsets||{}).length });

				if (!keep_redo) await this.setRedoData(data);

				// Handle both new and legacy structure for callback
				const upstreamData = data?.upstream?.signals || data?.upstreamData;
				const downstreamData = data?.downstream?.signals || data?.downstreamData;
				callback?.(upstreamData, downstreamData);
			})
			.finally(() => {
				if (seq === this.latest_req_seq) {
					this.loading = false;
					console.log("[TSD] updateTimeSpaceDiagram:finish", { seq });
					m.redraw();
				}
			});
	};

	// Which side of an Optimus job the diagram shows. The update endpoint does NOT recompute a job's
	// diagram from posted offsets — it hands back the job's own stored offset_input or offset_output,
	// picked by the parity of `seq` — so switching sides means asking for the other parity with no edit
	// attached, not moving the rows here and posting them.
	setOffsetSide = (side, callback) => {
		const wants_output = side === "after";
		if ((this.req_seq + 1) % 2 !== (wants_output ? 1 : 0)) this.req_seq += 1;
		this.offset_side = side;

		return this.updateTimeSpaceDiagram(callback, {});
	};

	exportData = () => {
		const before_data = this.before_data ?? this.init_data ?? this.current_data;
		const after_data = this.current_data;
		return client(`/api/v1/pages/space-time-export`, {
			method: "POST",
			body: {
				before_data,
				after_data,
				network_id: this.original_network?.id,
			},
		}).then((res) => {
			const signals = [];
			Object.getOwnPropertyNames(res?.signals).forEach((key) => {
				signals.push({ results: { ...res?.signals[key] }, signal_id: key });
			});
			const aoa_data = [
				// Sheet title row (spans all cols)
				[`${this.original_network?.name}`],
				// Header row
				TIMING_PLAN_DOWNLOAD_FIELDS.map((f) => f.title),
				// Data rows
				...this.original_network?.signals.map((sig) =>
					TIMING_PLAN_DOWNLOAD_FIELDS.map((f) => {
						if (f.source === "signal") {
							return sig[f.key];
						} else if (f.source === "job") {
							return f.key(maximizeID(sig.id), signals);
						} else {
							console.error(`Optimus Job Report Download: Unknown source for column field ${f.title}.`);
						}
					}),
				),
			];

			const impact_aoa_data = [];
			const IMPACT_DATA_COL_TITLES = [
				TIMING_PLAN_DOWNLOAD_FIELDS[0].title, // Signal ID
				TIMING_PLAN_DOWNLOAD_FIELDS[1].title, // Signal Name
				"Phase Number",
				TIMING_PLAN_DOWNLOAD_FIELDS[2].title, // Offset Before
				TIMING_PLAN_DOWNLOAD_FIELDS[3].title, // Offset After
				"Split (Before)",
				"Split (After)",
				"AOG (Before)",
				"AOG (After)",
				"AOY (Before)",
				"AOY (After)",
				"Platoon Ratio (Before)",
				"Platoon Ratio (After)",
				"Control Delay (Before)",
				"Control Delay (After)",
			];
			// Sheet title row (spans all cols)
			impact_aoa_data.push([`${this.original_network?.name}`]);
			// Header row
			impact_aoa_data.push(IMPACT_DATA_COL_TITLES);

			// Data rows
			this.original_network.signals.forEach((sig) => {
				const sig_res = signals?.find((signal) => signal.signal_id === maximizeID(sig.id))?.results;
				if (!sig_res) return;

				const phase_data_arrays = [];

				// Process phases from sig_res.phases (offsets)
				if (sig_res.phases) {
					Object.entries(sig_res.phases).forEach(([phase_number, phase_data]) => {
						const split_data = sig_res.splits?.[phase_number] || {};
						phase_data_arrays.push(
							createPhaseDataArray(sig, sig_res, phase_number, phase_data, split_data),
						);
					});
				}

				// Process additional phases from sig_res.splits with optimized_fl_control_delay_per_vehicle_mean_seconds
				if (sig_res.splits) {
					Object.entries(sig_res.splits).forEach(([phase_number, split_data]) => {
						if (!sig_res.phases?.[phase_number]) {
							const phase_data = {};
							phase_data_arrays.push(
								createPhaseDataArray(sig, sig_res, phase_number, phase_data, split_data),
							);
						}
					});
				}

				// Sort phases within the signal
				phase_data_arrays.sort((a, b) => a[2] - b[2]); // 2 is the new index of 'Phase Number'
				impact_aoa_data.push(...phase_data_arrays);
			});

			function createPhaseDataArray(sig, sig_res, phase_number, phase_data, split_data) {
				let phase_data_array = [];

				// Signal ID
				phase_data_array.push(sig[TIMING_PLAN_DOWNLOAD_FIELDS[0].key]);
				// Signal Name
				phase_data_array.push(sig[TIMING_PLAN_DOWNLOAD_FIELDS[1].key]);
				// Phase Number
				phase_data_array.push(phase_number);
				// Offset Before
				phase_data_array.push(TIMING_PLAN_DOWNLOAD_FIELDS[2].key(maximizeID(sig.id), signals));
				// Offset After
				phase_data_array.push(TIMING_PLAN_DOWNLOAD_FIELDS[3].key(maximizeID(sig.id), signals));

				// Split Before and After
				//if (split_data.optimized_fl_control_delay_per_vehicle_mean_seconds !== undefined) {
				phase_data_array.push(split_data.old || "--");
				phase_data_array.push(split_data.new || "--");
				//} else {
				//   phase_data_array.push('--', '--')
				// }

				// AOG, AOY, Platoon Ratio
				phase_data_array.push(phase_data?.old_aog ? numeralFormat(phase_data.old_aog, "0.0%") : "--");
				phase_data_array.push(phase_data?.new_aog ? numeralFormat(phase_data.new_aog, "0.0%") : "--");
				phase_data_array.push(phase_data?.old_aoy ? numeralFormat(phase_data.old_aoy, "0.0%") : "--");
				phase_data_array.push(phase_data?.new_aoy ? numeralFormat(phase_data.new_aoy, "0.0%") : "--");
				phase_data_array.push(
					phase_data?.old_platoon_ratio ? numeralFormat(phase_data.old_platoon_ratio, "0.00") : "--",
				);
				phase_data_array.push(
					phase_data?.new_platoon_ratio ? numeralFormat(phase_data.new_platoon_ratio, "0.00") : "--",
				);

				// Control Delay
				if (split_data.optimized_fl_control_delay_per_vehicle_mean_seconds !== undefined) {
					phase_data_array.push(
						split_data.fl_control_delay_per_vehicle_mean_seconds
							? `${numeralFormat(split_data.fl_control_delay_per_vehicle_mean_seconds, "0.00")}s`
							: "--",
					);
					phase_data_array.push(
						`${numeralFormat(split_data.optimized_fl_control_delay_per_vehicle_mean_seconds, "0.00")}s`,
					);
				} else {
					phase_data_array.push("--", "--");
				}

				return phase_data_array;
			}

			const wkbk = xlsx.utils.book_new();
			const impact_wkbk = xlsx.utils.book_new();
			const wkst = xlsx.utils.aoa_to_sheet([[]]);
			const impact_wkst = xlsx.utils.aoa_to_sheet([[]]);
			wkst["!merges"] = [
				// Header row spans all cols
				{
					s: { r: 0, c: 0 },
					e: { r: 0, c: TIMING_PLAN_DOWNLOAD_FIELDS.length - 1 },
				},
			];
			impact_wkst["!merges"] = [
				// Header row spans all cols
				{
					s: { r: 0, c: 0 },
					e: { r: 0, c: IMPACT_DATA_COL_TITLES.length - 1 },
				},
			];

			xlsx.utils.sheet_add_aoa(wkst, aoa_data);
			xlsx.utils.sheet_add_aoa(impact_wkst, impact_aoa_data);
			xlsx.utils.book_append_sheet(wkbk, wkst, "Timing Plan");
			xlsx.utils.book_append_sheet(impact_wkbk, impact_wkst, "Before & After");
			xlsx.writeFile(
				wkbk,
				convertToValidFilename(
					`flow-tsd-optimized-offsets-${this.original_network?.name}-timing-plan_${dayjs().format("YYYYMMDDHHmmss")}.csv`,
				),
				{
					bookType: "csv",
				},
			);
			xlsx.writeFile(
				impact_wkbk,
				convertToValidFilename(
					`flow-tsd-optimized-offsets-${this.original_network?.name}-before-after_${dayjs().format("YYYYMMDDHHmmss")}.csv`,
				),
				{
					bookType: "csv",
				},
			);
		});
	};


	getDataTimeSpaceFormJob = (job_id, new_offsets, job_tod_id) => {
		this.clearSession();
		this.loading_optimus = true;
		this.showModal = true;
		this.job_id = job_id;
		this.job_version = 1;
		return client(`/api/v1/pages/optimus/get-job-att-data/${job_id}`)
			.then(async (res) => {
				const end_date = dayjs.utc(res?.reference_date);
				const start_date = end_date.subtract(res?.lookback_period, "days");

				const params = {
					date_range: {
						period_days: res?.lookback_period,
						start_date: start_date.format("YYYY-MM-DD"),
						end_date: end_date.format("YYYY-MM-DD"),
						exclude_holidays: true,
					},
					tod_params: {
						local_start_time: res?.local_start_time,
						local_end_time: res?.local_end_time,
						has_monday: res?.has_monday,
						has_tuesday: res?.has_tuesday,
						has_wednesday: res?.has_wednesday,
						has_thursday: res?.has_thursday,
						has_friday: res?.has_friday,
						has_saturday: res?.has_saturday,
						has_sunday: res?.has_sunday,
						timezone_iana: res?.timezone_iana,
					},
					all_time: false,
				};


				await awaitAtspmRouting(this.original_network?.customer?.id);
				return client(`${atspmApiBase()}/pages/space-time/${this.original_network.id}`, {
					params,
				})
					.then(async (diagram_data) => {

						const data = await transformSpaceTimeData(diagram_data);
						await this.setData(data);
						await this.setCurrentData(diagram_data);
						await this.updateTimeSpaceDiagram(null, new_offsets);
						this.not_show_optimize = true;
					})
					.finally(() => {

						m.redraw();
					});
			})
			.finally(() => {
				this.job_tod_id = job_tod_id;
				this.job_id = job_id; // preserve job_id after chain
				this.loading_optimus = false;
				m.redraw();
			});
	};

	// New method for v2 jobs that uses the offset_input data directly
	getDataTimeSpaceFromJobV2 = (job_data, new_offsets) => {
		this.clearSession();
		this.loading_optimus = true;
		this.showModal = true;
		this.job_id = job_data?.job_id;
		this.job_version = 2;

		try {
			// Use the offset_input data directly from the job
			let diagram_data_before = job_data.offset_input;
			let diagram_data_after = job_data.offset_output;

			if (!diagram_data_before) {
				throw new Error("No offset_input data found in job");
			}

			// Create a deep copy to avoid modifying the original
			diagram_data_before = JSON.parse(JSON.stringify(diagram_data_before));

			// Fix upstream signal ordering for Optimus v2 jobs
			// The offset_input data from Optimus v2 has upstream signals in reverse order
			if (
				Array.isArray(diagram_data_before) &&
				diagram_data_before.length > 1 &&
				diagram_data_before[1] &&
				Array.isArray(diagram_data_before[1].signals)
			) {
				diagram_data_before[1].signals = diagram_data_before[1].signals.reverse();
			}

			if (!diagram_data_after) {
				throw new Error("No offset_output data found in job");
			}

			// Create a deep copy to avoid modifying the original
			diagram_data_after = JSON.parse(JSON.stringify(diagram_data_after));

			// Fix upstream signal ordering for Optimus v2 jobs
			if (
				Array.isArray(diagram_data_after) &&
				diagram_data_after.length > 1 &&
				diagram_data_after[1] &&
				Array.isArray(diagram_data_after[1].signals)
			) {
				diagram_data_after[1].signals = diagram_data_after[1].signals.reverse();
			}
			// The offset payload carries only the through phase's green and red. Fill in its yellow and red
			// clearance, and its ring-mate left turn, from the plan the job returned — same four
			// indications the ATSPM diagram draws, and the Left Turns column reads the same fields.
			this.optimus_plan = job_data.split_output;
			// The offset a signal ran BEFORE the job. The TSD payload carries none — `plan_offset` only
			// appears once the update endpoint has answered — so it comes off the job details: the offset
			// constraints the job was submitted with, which is the same number the Request Summary shows,
			// falling back to the plan on the job's own split input.
			this.optimus_before_offsets = this.buildBeforeOffsets(job_data);
			// The phase sequence each signal was submitted with, so the diagram orders its rings — and
			// reads each left turn's lead or lag — off the job rather than off an estimate.
			this.optimus_sequences = this.buildSequences(job_data);
			// Kept untouched: `current_data` moves with every edit, so the optimizer's own offsets need a
			// copy of their own for Fine Tuning Mode to measure a delta against.
			this.optimus_offset_output = JSON.parse(JSON.stringify(diagram_data_after));
			applyOptimusIndications(
				diagram_data_after,
				job_data.split_output,
				this.optimusSplitDelta,
				this.optimus_source,
				this.optimusSequences(),
				this.sequences,
			);
			applyOptimusIndications(diagram_data_before, job_data.split_input, null, this.optimus_source, this.optimusSequences(), this.sequences);

			const data_after = transformSpaceTimeData(diagram_data_after);
			const data_before = transformSpaceTimeData(diagram_data_before);
			this.setData(data_after);
			this.setCurrentData(diagram_data_after);

			// Set init_data to the corrected offset_input (plan_offsets) for reset functionality
			this.setInitData(diagram_data_before);

			// Set undo_data to the offset_input (plan_offsets) as the baseline
			this.setUndoData(data_before);

			// If new_offsets is not provided or is empty, extract offsets from offset_output data
			if ((!new_offsets || Object.keys(new_offsets).length === 0) && diagram_data_after) {
				new_offsets = {};
				// Extract offsets from downstream signals in offset_output
				const downstreamSignals = diagram_data_after[0]?.signals || [];
				downstreamSignals.forEach((signal) => {
					if (signal.signal_id && signal.plan_offset !== undefined) {
						new_offsets[signal.signal_id] = parseInt(signal.plan_offset, 10);
					}
				});
			}

			if (new_offsets) {
				this.updateTimeSpaceDiagram(null, new_offsets);
			}

			this.not_show_optimize = true;
			this.job_tod_id = job_data.settings?.tod?.id;
		} catch (error) {
			console.error("Error loading TSD data from job v2:", error);
			NotifyModel.error("Failed to load Time Space Diagram data");
		} finally {
			this.loading_optimus = false;
			m.redraw();
		}
	};

	optimize = () => {
		const { customer_id, network_id, tod_id, optimization_type, weights, signals } = this.current_new_job_settings;
		this.loading_job = true;
		this.loading_job_done = true;
		return client(`/api/v1/pages/optimus/submit`, {
			method: "POST",
			body: {
				customer_id: customer_id,
				network_id: network_id,
				tod_id: tod_id,
				optimization_type: optimization_type,
				weights: weights,
				signals: signals,
			},
		})
			.then(async () => {
				m.redraw();
			})
			.catch((err) => console.error(err?.message === "[object Object]" ? JSON.stringify(err) : err))
			.finally(() => {
				this.loading_job_done = false;
				setTimeout(() => {
					this.loading_job = false;
					m.redraw();
				}, 5000);
				m.redraw();
			});
	};

	async fetchOptimusPreferences(network_id) {
		this.prefs_loading = true;

		return client(`/api/v1/pages/optimus/get-preferences`)
			.then(async (res) => {
				const new_prefs = res?.preferences ?? {};

				this.default_new_job_settings = Object.assign(this.default_new_job_settings, new_prefs[network_id]);
				if (this.current_new_job_settings == null) {
					this.current_new_job_settings = cloneDeep(this.default_new_job_settings);
				}

				this.prefs_loading = false;

				m.redraw();
			})
			.catch((err) => console.error(err?.message === "[object Object]" ? JSON.stringify(err) : err));
	}

	setInitData = (data) => (this.init_data = Array.isArray(data) ? [...data] : null);

	setData = (data) => {
		//console.log("[TSD] setData called", {
		//	hasData: !!data,
		//	downstreamSignals: data?.downstream?.signals?.length || data?.downstreamData?.length,
		//	stackTrace: new Error().stack.split('\n').slice(1, 4).map(line => line.trim())
		//});
		this.data = { ...data };
	};

	setUndoData = (data) => {
		// Create a deep copy
		const deepCopy = JSON.parse(JSON.stringify(data));
		this.undo_data = deepCopy;
	};

	setRedoData = (data) => {
		// Create a deep copy instead of a shallow copy
		this.redo_data = JSON.parse(JSON.stringify(data));
	};

	setCurrentData = (data) => {
		//console.log("[TSD] setCurrentData called", {
		//	hasData: !!data,
		//	dataLength: data?.length,
		//	stackTrace: new Error().stack.split('\n').slice(1, 4).map(line => line.trim())
		//});
		this.current_data = Array.isArray(data) ? [...data] : null;
	};

	// Clear all per-view TSD state so each entry point (historical network TSD
	// or an Optimus job TSD) starts fresh instead of inheriting the previous
	// view's data, job identity, or undo history. The request sequence RESTARTS
	// at 0 — its parity is semantic for Optimus jobs (the server returns
	// offset_output for odd seq, offset_input for even, which is how the
	// undo/redo before-after toggle works), so a fresh session must begin the
	// same way every time: first update call = seq 1 = offset_output (AFTER).
	// Any in-flight response from the previous session (seq >= 1 != 0) is
	// dropped as stale by the latest_req_seq check.
	clearSession = () => {
		this.data = null;
		this.init_data = null;
		this.current_data = null;
		this.before_data = null;
		this.undo_data = null;
		this.redo_data = null;
		this.offset_side = "after";
		this.loading = false;
		this.loading_optimus = false;
		this.loading_job = false;
		this.loading_job_done = false;
		this.not_show_optimize = false;
		this.job_id = null;
		this.optimus_plan = null;
		this.optimus_offset_output = null;
		this.optimus_before_offsets = null;
		this.optimus_sequences = null;
		this.job_version = null;
		this.job_tod_id = null;
		this.req_seq = 0;
		this.latest_req_seq = 0;
		this.sequences = {};
		this.sequence_estimates = {};
		this.seeded_from = null;
	};

	setShowModal = (bool) => (this.showModal = bool);

	setSequence = (signal_id, option, callback) => {
		// Like an offset edit: the first change pins the BEFORE column to the untouched diagram.
		if (this.undo_data == null) this.setUndoData(this.data);
		this.sequences = { ...this.sequences, [signal_id]: option };
		return this.updateTimeSpaceDiagram(callback);
	};

	setSequenceEstimates = (estimates) => (this.sequence_estimates = estimates || {});

	setView = (view) => (this.view = view);

	// Switching approach refetches from scratch, so any plan edits belong to data that no longer exists.
	setAggregatedSource = (source) => {
		if (source === this.aggregated_source) return;
		this.aggregated_source = source;
		this.clearPlanOverrides();
	};

	// The offset the optimizer returned for a signal, before any hand editing — `init_data` is the job's
	// own offset_input, so the after side lives on the untouched copy of offset_output taken at load.
	// Fine-tuned split deltas for this job, or zero when the mode is off — the diagram redraws its bars
	// against whatever the Split Analysis rows are currently showing.
	optimusSequences = () => {
		if (!this.optimus_sequences) this.optimus_sequences = this.buildSequences(this.loadedJob());

		return this.optimus_sequences;
	};

	optimusSplitDelta = (signal_id, phase_number) =>
		this.job_id != null && OptimusFineTuningModel.isApplied(this.job_id)
			? OptimusFineTuningModel.splitDelta(signal_id, phase_number)
			: 0;

	// The offset a signal ran before the job. Built when a job is loaded into the diagram, but rebuilt
	// from the job on demand: the editor can be reached without going through that loader, and an empty
	// column is worse than a second look at data already in hand.
	beforeOffset = (signal_id) => {
		if (!this.optimus_before_offsets) this.optimus_before_offsets = this.buildBeforeOffsets(this.loadedJob());

		return this.optimus_before_offsets[signal_id] ?? null;
	};

	loadedJob = () => (this.job_id ? OptimusModel.jobs?.find((job) => job.job_id === this.job_id) : null);

	buildSequences = (job) => {
		const sequences = {};
		for (const signal of job?.settings?.signals_optimization || []) {
			const sequence = signal?.offset_constraints?.sequence;
			if (signal?.id == null || !sequence?.rings?.length) continue;

			for (const form of [signal.id, minimizeID(signal.id), maximizeID(signal.id)]) {
				if (form) sequences[form] = sequence;
			}
		}

		return sequences;
	};

	buildBeforeOffsets = (job) => {
		const offsets = {};
		if (!job) return offsets;

		// Ids arrive minimized on the job details and raw from the update-offsets endpoint, so every entry
		// goes in under both forms.
		const put = (signal_id, offset) => {
			for (const form of [signal_id, minimizeID(signal_id), maximizeID(signal_id)]) {
				if (form) offsets[form] = offset;
			}
		};

		for (const [signal_id, signal] of Object.entries(job.split_input || {})) {
			const offset = signal?.plan_offset ?? signal?.offset;
			if (offset != null) put(signal_id, offset);
		}

		// The offset constraints the job was submitted with — the same number the Request Summary lists.
		for (const signal of job.settings?.signals_optimization || []) {
			const offset = signal?.offset_constraints?.current_offset;
			if (signal?.id != null && offset != null) put(signal.id, offset);
		}

		return offsets;
	};

	optimizedOffset = (signal_id) => {
		for (const route of this.optimus_offset_output || []) {
			for (const signal of route?.signals || []) {
				if (signal?.signal_id === signal_id && signal?.plan_offset != null) return signal.plan_offset;
			}
		}

		return null;
	};

	// Switching the bar source rebuilds the indications on the data already loaded — the offsets and the
	// diagram's geometry do not change, only what the bars are drawn from.
	setOptimusSource = (source, callback) => {
		if (this.optimus_source === source) return;
		this.optimus_source = source;
		if (!this.current_data || !this.optimus_plan) return;

		const rebuilt = JSON.parse(JSON.stringify(this.current_data));
		applyOptimusIndications(rebuilt, this.optimus_plan, this.optimusSplitDelta, source, this.optimusSequences(), this.sequences);
		const data = transformSpaceTimeData(rebuilt);
		this.setData(data);

		callback?.(data?.upstream?.signals || data?.upstreamData, data?.downstream?.signals || data?.downstreamData);
		m.redraw();
	};

	setLoadingJob = (bool) => (this.loading_job = bool);

	setOriginalNetwork = (data) => (this.original_network = data);

	reset = async (callback) => {
		this.sequences = {};
		const data = transformSpaceTimeData(this.init_data);

		await this.setCurrentData(this.init_data);
		await this.setData(data);

		// Handle both new and legacy structure for callback
		const upstreamData = data?.upstream?.signals || data?.upstreamData;
		const downstreamData = data?.downstream?.signals || data?.downstreamData;
		callback?.(upstreamData, downstreamData);
		this.job_tod_id = null;
		this.loading_job = false;
		this.loading_job_done = false;
		this.not_show_optimize = false;
	};
}

export default new SpaceTime();
